import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/view/components/ui/use-toast";
import { validateCpf } from "@/app/utils/cpfFormater";
import { useEffect, useState } from "react";
import { useC6Bank } from "@/app/contexts/C6BankContext";
import { c6BankService } from "@/app/services/c6BankService";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";

const formatPhoneValue = (phone?: string) => {
  const value = (phone || "").replace(/\D/g, "").slice(0, 11);

  if (!value) return "";

  const ddd = value.slice(0, 2);
  const rest = value.slice(2);

  if (rest.length > 5) {
    return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
  }

  if (rest.length > 0) {
    return `(${ddd}) ${rest}`;
  }

  return `(${ddd}`;
};

const formatBirthDateValue = (birthDate?: string) => {
  if (!birthDate) return "";

  const [year, month, day] = birthDate.slice(0, 10).split("-");

  if (!year || !month || !day) return "";

  return `${day}/${month}/${year}`;
};

const getGenerateAuthorizationSchema = z.object({
  cpf: z
    .string()
    .min(11, "CPF é obrigatório")
    .refine((v) => validateCpf(v), "CPF Inválido")
    .transform((f) => f.replace(/\D/g, "")),
  name: z.string().min(1, "Nome é obrigatório"),
  email: z.string().min(1, "E-mail é obrigatório"),
  birthDate: z
    .string()
    .min(1, "Data de nascimento é obrigatória")
    .refine((value) => {
      const [day, month, year] = value.split("/");

      if (!day || !month || !year) return false;

      const birthDate = new Date(Number(year), Number(month) - 1, Number(day));

      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 21);

      return birthDate <= minDate;
    }, "A data precisa ser superior a 21 anos de idade"),
  phone: z.string().min(1, "Telefone é obrigatório"),
});

type getGenerateAuthorizationFormData = z.infer<
  typeof getGenerateAuthorizationSchema
>;

interface UseGenerateAuthorizationModalProps {
  onContinue?: () => void;
}

export default function useGenerateAuthorizationModal({
  onContinue,
}: UseGenerateAuthorizationModalProps) {
  const getGenerateAuthorizationForm =
    useForm<getGenerateAuthorizationFormData>({
      resolver: zodResolver(getGenerateAuthorizationSchema),
      mode: "onChange",
    });

  const { handleSubmit, watch, setValue } = getGenerateAuthorizationForm;

  const { toast } = useToast();

  const { setAuthorization } = useC6Bank();

  const [loading, setLoading] = useState(false);

  const cpf = watch("cpf");
  const cpfClean = cpf?.replace(/\D/g, "");

  const {
    data: customer,
    isSuccess,
    isFetching: isFetchingCustomer,
  } = useCustomerCpf(cpfClean);

  useEffect(() => {
    if (isSuccess && customer) {
      const setField = (
        field: "name" | "email" | "phone" | "birthDate",
        value?: string,
      ) => {
        if (value) {
          setValue(field, value, { shouldValidate: true });
        }
      };

      setField("name", customer.name);
      setField("email", customer.email);
      setField("phone", formatPhoneValue(customer.phone));
      setField("birthDate", formatBirthDateValue(customer.birthDate));
    }
  }, [isSuccess, customer, setValue]);

  const submitModal = handleSubmit(
    async (formData: getGenerateAuthorizationFormData) => {
      try {
        setLoading(true);

        const formatBirthDate = (date: string) => {
          const [day, month, year] = date.split("/");

          return `${year}-${month}-${day}`;
        };

        const payload = {
          cpf: formData.cpf.replace(/\D/g, ""),
          phone: formData.phone.replace(/\D/g, ""),
          birthDate: formatBirthDate(formData.birthDate),
          name: formData.name,
          email: formData.email,
        };

        const response = await c6BankService.postAuthorization(payload);

        setAuthorization({
          id: response.id,
          link: response.link,
          expirationDate: response.expirationDate,
        });

        onContinue?.();
      } catch (error: any) {
        toast({
          title: "Não foi possível gerar a autorização",
          description: error?.message || "Erro ao gerar autorização",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
  );

  return {
    form: getGenerateAuthorizationForm,
    submitModal,
    loading,
    isFetchingCustomer,
  };
}
